import { useState, useEffect } from 'react';
import { formatHourLabel } from '../utils/bookingUtils';
import { useConfig } from '../context/ConfigContext';
import { formatMoney } from '../utils/currencyUtil';
import { getSessionCourtNumbers, getSessionTimeRangeInfo, formatSessionRangeText, subscribeSessionJoins } from '../utils/openPlayUtil';
import '../css/OpenPlaySessionsSection.css';

const DOW_S = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "Wed, Aug 12" — no year, for this compact card (formatSessionDateLabel in
// openPlayUtil.js keeps the year for the longer "coming soon" modal text
// instead — see App.jsx). Parsed manually from "yyyy-MM-dd" as a local date
// rather than `new Date(dateStr)`, same reasoning as every other date-badge
// helper in this app: avoids that string form being read as UTC midnight.
const formatCardDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DOW_S[dt.getDay()]}, ${MON_S[dt.getMonth()]} ${dt.getDate()}`;
};

/**
 * One "Hosted open play sessions" / "Open Play sessions" card. Two visual
 * variants sharing the same data (see AdminTimeSlotSelection.jsx and
 * TimeSlotSelection.jsx for how each is wired):
 *   - 'admin' (default): the whole card is the click target (opens
 *     AdminHostOpenPlayModal in edit mode) — fee/max players/joining rows
 *     plus a confirmed-vs-awaiting progress bar.
 *   - 'guest': only the "Join this session" button is the click target
 *     (shows the session's details + the "coming soon" notice — see
 *     App.jsx's handleOpenPlaySessionClick) — a "Court" pill row, a "Per
 *     player" fee row, and a "X of Y joined / Z slots left" line instead of
 *     the admin's progress bar.
 * @param {object} session - an openPlaySessions/{id} record, including its Firebase `id`
 * @param {'admin'|'guest'} variant
 * @param {(session: object) => void} onClick
 * @param {((session: object, breakdown: {confirmed: number, awaiting: number, total: number}) => void)|null} onDelete
 *   admin only. Passing it renders the cancel control; the live player
 *   breakdown goes up with the session because the caller needs it to choose
 *   which confirmation to show, and this card is the only thing subscribed to
 *   it.
 */
function OpenPlaySessionCard({ session, variant = 'admin', onClick, onDelete = null }) {
  const { config } = useConfig();
  const courts = getSessionCourtNumbers(session);
  const { startHour, endHour, variesByCourt, endsNextDay } = getSessionTimeRangeInfo(session);
  const [breakdown, setBreakdown] = useState({ confirmed: 0, awaiting: 0, total: 0 });

  // Live-subscribed rather than fetched once on mount: an admin adding a
  // player from AdminHostOpenPlayModal writes a new openPlayJoins record
  // without changing this session, so a one-shot fetch keyed on session.id
  // never re-ran and the "Joining" figure went stale until a full reload.
  // subscribeSessionJoins already drops declined joins, so everything it
  // returns counts toward the total.
  useEffect(() => {
    const unsubscribe = subscribeSessionJoins(session.id, (joins) => {
      let confirmed = 0;
      let awaiting = 0;
      joins.forEach((j) => {
        const n = Number(j.numPlayers) || 0;
        if (j.status === 'confirmed') confirmed += n;
        else awaiting += n;
      });
      setBreakdown({ confirmed, awaiting, total: confirmed + awaiting });
    });
    return () => unsubscribe();
  }, [session.id]);

  const maxPlayers = Number(session.maxPlayers) || 0;
  const fee = Number(session.fee) || 0;
  const slotsLeft = Math.max(0, maxPlayers - breakdown.total);
  const confirmedPct = maxPlayers > 0 ? Math.min(100, (breakdown.confirmed / maxPlayers) * 100) : 0;
  const awaitingPct = maxPlayers > 0 ? Math.min(100 - confirmedPct, (breakdown.awaiting / maxPlayers) * 100) : 0;

  const top = (
    <div className="opsc-top">
      <span className="opsc-badge">OPEN PLAY</span>
      <span className="opsc-top-right">
        <span className="opsc-courts-count">{courts.length} court{courts.length === 1 ? '' : 's'}</span>
        {onDelete && (
          // stopPropagation, or cancelling would also fire the card's own
          // click and open the edit modal behind the confirmation.
          <button
            type="button"
            className="opsc-delete"
            aria-label="Cancel this Open Play session"
            title="Cancel this Open Play session"
            onClick={(e) => { e.stopPropagation(); onDelete(session, breakdown); }}
          >
            &times;
          </button>
        )}
      </span>
    </div>
  );
  const dateTime = (
    <>
      <div className="opsc-date">{formatCardDate(session.date)}</div>
      {startHour !== null && (
        <div className="opsc-time">
          {formatSessionRangeText(startHour, endHour, endsNextDay)}
          {variesByCourt && <span className="opsc-varies"> &middot; varies by court</span>}
        </div>
      )}
    </>
  );
  const pills = (
    <div className="opsc-pills">
      {courts.map((c) => (
        <span key={c} className={`opsc-pill opsc-pill-${((c - 1) % 6) + 1}`}>{c}</span>
      ))}
    </div>
  );

  if (variant === 'guest') {
    return (
      <div className="opsc-card opsc-card-guest">
        {top}
        {dateTime}
        <div className="opsc-court-line">
          <span className="opsc-court-line-label">Court</span>
          {pills}
        </div>
        <div className="opsc-divider" />
        <div className="opsc-row">
          <span>Per player</span>
          <span className="opsc-value">{formatMoney(fee, config)}</span>
        </div>
        <div className="opsc-row opsc-row-joined">
          <span>{breakdown.total} of {maxPlayers} joined</span>
          <span className="opsc-slots-left">{slotsLeft} slots left</span>
        </div>
        <button type="button" className="opsc-join-btn" onClick={() => onClick?.(session)}>
          Join this session
        </button>
      </div>
    );
  }

  // Deliberately a <div role="button"> rather than a real <button>:
  // global.css has a blanket `button { max-height: 50px; }` rule, which
  // hard-caps a multi-row card like this one to the height of its first
  // row and makes everything below it overflow outside the card's own
  // border. The guest variant below has always been a <div> for unrelated
  // reasons, which is why only this admin variant was affected.
  return (
    <div
      role="button"
      tabIndex={0}
      className="opsc-card opsc-card-admin"
      onClick={() => onClick?.(session)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(session);
        }
      }}
    >
      {top}
      {dateTime}
      {pills}
      <div className="opsc-divider" />
      <div className="opsc-row">
        <span>Fee</span>
        <span className="opsc-value">{formatMoney(fee, config)}</span>
      </div>
      <div className="opsc-row">
        <span>Max players</span>
        <span className="opsc-value">{maxPlayers}</span>
      </div>
      <div className="opsc-row">
        <span>Joining</span>
        <span className="opsc-value">{breakdown.total} / {maxPlayers}</span>
      </div>
      <div className="opsc-progress" role="progressbar" aria-valuenow={breakdown.total} aria-valuemax={maxPlayers}>
        <div className="opsc-progress-confirmed" style={{ width: `${confirmedPct}%` }} />
        <div className="opsc-progress-awaiting" style={{ width: `${awaitingPct}%` }} />
      </div>
    </div>
  );
}

export default OpenPlaySessionCard;
